import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PlayDayCard from 'components/play/home/PlayDayCard';
import { DAY_VALUES, intensityOf, itemsOfDay, todayIso } from 'components/play/constants';

// Copias de la semana que se pintan en el carrusel. Con 3 siempre queda una semana entera de
// colchón a cada lado, así que el reposicionamiento nunca cae dentro de lo que se está viendo.
const LOOP_COPIES = 3;

/**
 * OTRIX Play — tira con los 7 días de la semana.
 *
 * En desktop reparte los días en una rejilla fija (lunes → domingo). En mobile 7 tarjetas no
 * caben y apilarlas enterraría el resto de la pantalla, así que es un carrusel horizontal con
 * scroll-snap que:
 *  - **empieza en HOY** (los días se reordenan desde hoy, no desde el lunes), y
 *  - **no tiene final**: al acercarse a un extremo se reposiciona en la copia central, de modo
 *    que se puede seguir deslizando en las dos direcciones indefinidamente.
 */
function PlayWeekStrip({ items = [], days = [] }) {
  const iso = todayIso();
  const theme = useTheme();
  const isCarousel = useMediaQuery(theme.breakpoints.down('lg'));
  const trackRef = useRef(null);

  // Semana reordenada desde hoy: hoy, mañana, … Aritmética modular sobre 1..7, el mismo patrón
  // que ya usa el carrusel de noticias destacadas.
  const weekFromToday = useMemo(
    () => DAY_VALUES.map((_, index) => ((index + iso - 1) % 7) + 1),
    [iso]
  );

  // En desktop se pinta UNA semana en orden natural: allí la rejilla `repeat(7, 1fr)` deja de
  // ser carrusel y 21 tarjetas se desbordarían a tres filas.
  // Se pinta `{ key, day }` y no el número suelto porque en el carrusel el mismo día aparece
  // una vez por copia y React necesita claves distintas.
  const renderedDays = useMemo(() => {
    if (!isCarousel) return DAY_VALUES.map((day) => ({ key: `w0-${day}`, day }));
    return Array.from({ length: LOOP_COPIES }, (_, copy) =>
      weekFromToday.map((day) => ({ key: `w${copy}-${day}`, day }))
    ).flat();
  }, [isCarousel, weekFromToday]);

  // Ancho EXACTO de una semana medido en el DOM (7 tarjetas + 7 huecos). Calcularlo como
  // `scrollWidth / 3` se deja un hueco por fuera y el bucle acumularía deriva.
  const weekWidth = useCallback((track) => {
    const first = track.children[0];
    const next = track.children[DAY_VALUES.length];
    if (!first || !next) return 0;
    return next.offsetLeft - first.offsetLeft;
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !isCarousel) return undefined;

    // Arranque: la copia CENTRAL, con hoy pegado al borde izquierdo.
    const period = weekWidth(track);
    if (!period) return undefined;
    track.scrollLeft = period;

    let frame = null;
    const onScroll = () => {
      // El evento de scroll se dispara decenas de veces por gesto; con rAF se mide y se
      // reposiciona una sola vez por frame, en lugar de forzar un reflow en cada uno.
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const span = weekWidth(track);
        if (!span) return;

        // El contenido se repite cada `span` píxeles, así que saltar un múltiplo exacto es
        // invisible: queda la misma tarjeta en el mismo sitio y se puede seguir deslizando.
        if (track.scrollLeft < span * 0.5) {
          track.scrollLeft += span;
        } else if (track.scrollLeft > span * 2) {
          track.scrollLeft -= span;
        }
      });
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [isCarousel, iso, weekWidth]);

  return (
    <Box
      ref={trackRef}
      sx={{
        display: 'grid',
        gap: 1.5,
        gridAutoFlow: { xs: 'column', lg: 'unset' },
        gridAutoColumns: { xs: '78%', sm: '42%', md: '30%', lg: 'unset' },
        gridTemplateColumns: { lg: 'repeat(7, 1fr)' },
        overflowX: { xs: 'auto', lg: 'visible' },
        scrollSnapType: { xs: 'x mandatory', lg: 'none' },
        // El scroll horizontal no debe recortar el hover translateY de las tarjetas.
        px: 0.25,
        py: 0.5,
        mx: -0.25,
        // `start` y no `center`: "hoy primero" es hoy pegado al borde, con un adelanto de mañana.
        // Y hace que la posición de arranque coincida con el punto de anclaje, sin saltos.
        '& > *': { scrollSnapAlign: { xs: 'start', lg: 'none' } },
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'hairline.strong', borderRadius: 3 }
      }}>
      {renderedDays.map(({ key, day }) => (
        <Box key={key} sx={{ minWidth: 0, display: 'flex' }}>
          <PlayDayCard
            day={day}
            items={itemsOfDay(items, day)}
            intensity={intensityOf(days, day)}
            isToday={day === iso}
          />
        </Box>
      ))}
    </Box>
  );
}

PlayWeekStrip.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  /** Filas de día de la rutina ({ day_of_week, intensity }) = los días de entreno elegidos. */
  days: PropTypes.arrayOf(PropTypes.object)
};

export default PlayWeekStrip;
